<template>
  <div>
    <div
      class="bg-white px-[15px] py-[24px] sm:px-[24px] sm:py-[32px] mt-[16px] rounded-[4px]"
    >
      <div>
        <h3
          class="text-[20px] leading-[20px] text-[#2463EB] font-bold mb-[24px]"
        >
          {{ customer.displayName }}
        </h3>
      </div>
      <div
        class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2"
      >
        <h2
          class="font-normal text-[16px] text-[#96a0b5] md:max-w-[170px] w-full"
        >
          Account
        </h2>
        <span
          class="font-medium text-[16px] text-[#353535] leading-[19.36px]"
          >{{ customer.code }}</span
        >
      </div>
      <div
        class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2"
      >
        <h2
          class="font-normal text-[16px] text-[#96a0b5] md:max-w-[170px] w-full"
        >
          Account Balance
        </h2>
        <span
          class="font-medium text-[16px] text-[#353535] leading-[19.36px]"
          :class="[
            { 'text-success': customer.accountBalance > 0 },
            { 'text-danger': customer.accountBalance < 0 },
          ]"
          >&#8358;{{ formatMoney(customer.accountBalance) }}</span
        >
      </div>
      <div
        class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2"
      >
        <h2
          class="font-normal text-[16px] text-[#96a0b5] md:max-w-[170px] w-full"
        >
          Delivery Address
        </h2>
        <span
          class="font-medium text-[16px] text-[#353535] leading-[19.36px]"
          >{{ customer.deliveryAddress }}</span
        >
      </div>
      <div
        class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2"
      >
        <h2
          class="font-normal text-[16px] text-[#96a0b5] md:max-w-[170px] w-full"
        >
          Start Date
        </h2>
        <span
          class="font-medium text-[16px] text-[#353535] leading-[19.36px]"
          >{{
            formatDate(transactionFilter.startDate)
              ? formatDate(transactionFilter.startDate)
              : "***"
          }}</span
        >
      </div>
      <div
        class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2"
      >
        <h2
          class="font-normal text-[16px] text-[#96a0b5] md:max-w-[170px] w-full"
        >
          End Date
        </h2>
        <span
          class="font-medium text-[16px] text-[#353535] leading-[19.36px]"
          >{{
            formatDate(transactionFilter.endDate)
              ? formatDate(transactionFilter.endDate)
              : "***"
          }}</span
        >
      </div>
    </div>
    <div
      class="bg-white px-[15px] py-[24px] sm:px-[24px] sm:py-[32px] mt-[16px] rounded-[4px]"
    >
      <div class="table-responsive">
        <table class="table m-0 p-0 text-nowrap">
          <thead class="bg-[#ECF9FF]">
            <tr>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Transaction Date
              </th>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Value Date
              </th>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Transaction Type
              </th>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Reference ID
              </th>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Status
              </th>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Balance Impact
              </th>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Amount
              </th>
              <th
                class="text-[12px] leading-[18px] text-[#000000] font-semibold"
              >
                Balance
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr v-if="loading">
              <td colspan="12" class="text-center">
                <div
                  class="text-center"
                  style="padding-top: 100px; padding-bottom: 50px"
                >
                  <span
                    class="fa-solid fa-spinner fa-spin text-[#2463EB]"
                    style="font-size: 8rem"
                  ></span>
                  <h3
                    class="text-center pt-[24px] text-[16px] font-medium leading-[16.94px]"
                    style="padding-bottom: 100px"
                  >
                    Please wait while we loading the customer's transactions
                  </h3>
                </div>
              </td>
            </tr>
            <tr v-for="t in transactions.data" :key="t.id" v-else>
              <td
                class="text-[14px] font-medium leading-[16.94px] text-[#353535]"
              >
                {{ formatDate(t.createdAt) }}
              </td>
              <td
                class="text-[14px] font-medium leading-[16.94px] text-[#353535]"
              >
                {{ formatDate(t.valueDate) }}
              </td>
              <td
                class="text-[14px] font-medium leading-[16.94px] text-[#353535]"
              >
                {{ getTransactionType(t.transactionType) }}
              </td>
              <td
                class="text-[14px] font-medium leading-[16.94px] text-[#353535]"
              >
                {{ t.referenceId }}
              </td>
              <td>
                <span
                  class="flex items-center gap-[4px] max-w-max w-full"
                  :class="[
                    'badge',
                    {
                      'badge-success':
                        getStatus(t.status) == 0
                          ? 'badge-danger'
                          : !getStatus(t.status) == 0,
                    },
                  ]"
                >
                  <span
                    class="w-[10px] h-[10px] rounded-[1px]"
                    :style="{
                      backgroundColor:
                        !getStatus(t.status) == 0 ? '#16a249' : '#EF5253',
                    }"
                  ></span>
                  <span class="text-[12px] font-normal leading-[16.94px]">{{
                    !getStatus(t.status) == 0 ? "Completed" : "Pending"
                  }}</span>
                </span>
              </td>
              <td
                class="text-[14px] font-medium leading-[16.94px] text-[#353535]"
              >
                {{ getBalanceImpact(t.balanceImpact) }}
              </td>
              <td
                class="text-[14px] font-medium leading-[16.94px] text-[#353535]"
              >
                &#8358; {{ formatMoney(t.amount) }}
              </td>
              <td
                class="text-[14px] font-medium leading-[16.94px] text-[#353535]"
              >
                &#8358; {{ formatMoney(t.parentBalance) }}
              </td>
            </tr>
            <tr v-if="!transactions.data.length && !loading">
              <td colspan="8">
                <div class="text-center" style="padding-top: 50px">
                  <span
                    style="font-size: 4.615rem"
                    class="mdi mdi-account-card-details"
                  ></span>
                </div>
                <h3 class="text-center" style="padding-bottom: 100px">
                  User doesn't have any transaction.
                </h3>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination
          :limit="transactions.pageSize"
          :count="transactions.pageCount"
          :page="transactions.pageNumber"
          :itemsCount="transactions.count"
          @changePage="gotoPage"
          @changeLimit="changeLimit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { helper } from "@/helper";
import Pagination from "../Pagination.vue";
export default {
  name: "CustomerTransaction",
  props: ["customerId"],
  components: {
    Pagination,
  },
  computed: {
    ...mapGetters(["transactions", "transactionFilter"]),
    loading() {
      return this.$store.state.loading;
    },
    currentBalance: function() {
      let balance = 0;
      if (
        this.customer &&
        this.transactions.data &&
        this.transactions.data.length > 0
      ) {
        balance = -this.customer.accountBalance;
        this.transactions.data.forEach((element) => {
          balance =
            element.balanceImpact === 0
              ? balance + element.amount
              : balance - element.amount;
        });
      }
      return balance;
    },

    user: function() {
      return this.$store.state.currentUser;
    },
  },
  data() {
    return {
      isBusy: false,
      isDownloading: false,
      customer: {},
      transactionTypes: [
        { name: "All Transactions", value: "" },
        { name: "Payment", value: 0 },
        { name: "Product Purchase", value: 1 },
        { name: "Adjustment Credit Note", value: 2 },
        { name: "Adjustment Debit Note", value: 3 },
        { name: "Reversal", value: 4 },
        { name: "Invoice Payment", value: 5 },
        { name: "Funds Transfer", value: 6 },
        { name: "Product Returns", value: 7 },
        { name: "Product Returns Reversal", value: 8 },
      ],

      balanceImpacts: [
        { name: "Credit", value: 0 },
        { name: "Debit", value: 1 },
      ],

      status: [
        { name: "Completed", value: 0 },
        { name: "Reversed", value: 1 },
      ],
    };
  },

  created() {
    this.getSalesRep();
  },
  mounted() {
    this.getTransactions();
  },
  beforeDestroy() {
    this.$store.commit("resetTransactionState", {
      pageNumber: 1,
      pageSize: this.transactions.pageSize,
    });
  },
  methods: {
    ...mapActions(["getTransactions"]),
    getTransactions() {
      const id = this.customerId;
      this.$store.dispatch("getTransactions", id);
    },
    async getSalesRep() {
      const id = this.customerId;
      try {
        const response = await this.$http.get("/Users/getById/" + id);
        if (response.ok) this.customer = response.body;
      } catch (error) {}
    },

    getTransactionType(transactionType) {
      const result = this.transactionTypes.find(
        (element) => element.value === transactionType
      );
      if (result) return result.name;
    },

    getBalanceImpact(balanceImpact) {
      return this.balanceImpacts.find(
        (element) => element.value === balanceImpact
      ).name;
    },

    getStatus(status) {
      return this.status.find((element) => element.value === status).name;
    },

    formatDate(date) {
      if (date) return moment(date).format("ll");
      else return "";
    },

    formatMoney(val) {
      return helper.formatMoney(val);
    },
    prev() {
      if (this.page > 0) {
        this.transactions.pageNumber -= 1;
        this.getTransactions();
      }
    },

    next() {
      if (hasNext) {
        this.transactions.pageNumber += 1;
        this.getTransactions();
      }
    },
    gotoPage(page) {
      this.transactions.pageNumber = page;
      this.getTransactions();
    },

    changeLimit(limit) {
      this.transactions.pageSize = limit;
      this.pageNumber = 1;
      this.getTransactions();
    },
  },
};
</script>
